/* import __COLOCATED_TEMPLATE__ from './procedure-list-item-stats.hbs'; */
/* RESPONSIBLE TEAM: team-actions */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { get } from 'embercom/lib/ajax';
import type AppService from 'embercom/services/app-service';
import type { DataConnectorHealthEntry } from './procedure-data-connector-health';

type ConnectorHealthStampColor = 'green' | 'yellow' | 'red';

interface ConnectorHealthStatus {
  status: string;
  has_connectors: boolean;
}

interface ProcedureListItemStatsSignature {
  Args: {
    procedureId: string;
    showFailures?: boolean;
    isProcedure?: boolean;
    connectorHealthStatuses?: Record<string, ConnectorHealthStatus>;
  };
  Element: HTMLDivElement;
}

// All stats use the same count metric, filtered by resolution state
const METRIC_ID = 'fin.conversations_participated.count';

type StatType = 'triggered' | 'resolved' | 'handoff' | 'escalated' | 'pending' | 'failed';

export default class TasksProcedureListItemStats extends Component<ProcedureListItemStatsSignature> {
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;

  @tracked selectedProcedureId?: string;
  @tracked selectedStat?: StatType;
  @tracked isConnectorDrawerOpen = false;
  @tracked dataConnectorHealth: DataConnectorHealthEntry[] = [];
  @tracked isLoadingHealth = false;

  get connectorHealthStatus(): ConnectorHealthStatus | undefined {
    return this.args.connectorHealthStatuses?.[this.args.procedureId];
  }

  get showConnectorHealth(): boolean {
    return (
      this.args.isProcedure === true &&
      this.appService.app.canUseFeature('team-actions-procedure-data-connector-health') &&
      !!this.connectorHealthStatus?.has_connectors
    );
  }

  get connectorHealthColor(): ConnectorHealthStampColor {
    if (!this.connectorHealthStatus) {
      return 'green';
    }
    switch (this.connectorHealthStatus.status) {
      case 'unhealthy':
        return 'red';
      case 'degraded':
        return 'yellow';
      default:
        return 'green';
    }
  }

  get connectorHealthLabel(): string {
    return this.connectorHealthStatus?.status ?? 'healthy';
  }

  drillInColumns = [
    'conversation.id',
    'conversation.first_user_conversation_part_created_at',
    'conversation.fin.resolution_state',
    'conversation.channel_type',
    'conversation.bot_conversation_rating.rating_index',
    'conversation.fin.procedures_triggered.procedure_ids',
  ];

  get canAdminAccessReporting(): boolean {
    return this.permissionsService.currentAdminCan('can_access_reporting');
  }

  get canUseProcedureErrorReporting(): boolean {
    return this.appService.app.canUseFeature('procedures-error-reporting');
  }

  get showFailedStat(): boolean {
    return this.canUseProcedureErrorReporting && this.args.showFailures !== false;
  }

  get chartDefinitions(): Record<string, any> {
    let definitions: Record<string, any> = {
      triggered: this.chartDefinitionForStat('triggered'),
      resolved: this.chartDefinitionForStat('resolved'),
      handoff: this.chartDefinitionForStat('handoff'),
      escalated: this.chartDefinitionForStat('escalated'),
      pending: this.chartDefinitionForStat('pending'),
    };

    if (this.showFailedStat) {
      definitions.failed = this.chartDefinitionForStat('failed');
    }

    return definitions;
  }

  @action
  openSideDrawer(procedureId: string, stat: StatType, event: Event) {
    this.trackEvent(stat);
    event.stopPropagation();
    event.preventDefault();
    if (this.permissionsService.currentAdminCan('can_reporting__drillin__access')) {
      this.selectedProcedureId = procedureId;
      this.selectedStat = stat;
    } else {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
    }
  }

  @action
  closeSideDrawer() {
    this.selectedProcedureId = undefined;
    this.selectedStat = undefined;
  }

  @action
  openConnectorDrawer(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.isConnectorDrawerOpen = true;
    this.fetchDataConnectorHealth();
  }

  @action
  closeConnectorDrawer() {
    this.isConnectorDrawerOpen = false;
  }

  @action
  trackEvent(stat_clicked: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'procedure_stat',
      place: 'procedure_list_item',
      section: 'procedures',
      stat: stat_clicked,
    });
  }

  private chartDefinitionForStat(stat: string): any {
    let procedureId = this.args.procedureId;
    let procedureProperty =
      stat === 'failed'
        ? 'fin.procedures_failed.procedure_ids'
        : 'fin.procedures_triggered.procedure_ids';

    let filters: any[] = [
      {
        type: 'category',
        data: {
          property: procedureProperty,
          values: [procedureId],
        },
      },
    ];

    if (stat === 'resolved') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['confirmed_resolved', 'assumed_resolved'],
        },
      });
    } else if (stat === 'handoff') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['procedure_handoff'],
        },
      });
    } else if (stat === 'escalated') {
      filters.push({
        type: 'category',
        data: {
          property: 'fin.resolution_state',
          values: ['routed_to_team'],
        },
      });
    } else if (stat === 'pending') {
      filters.push({
        type: 'not_exists',
        data: {
          property: 'fin.resolution_state',
        },
      });
    }

    return {
      id: `procedure_${procedureId}_${stat}`,
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id: METRIC_ID,
          filters: {
            type: 'and',
            filters,
          },
        },
      ],
    };
  }

  private async fetchDataConnectorHealth(): Promise<void> {
    if (this.isLoadingHealth || this.dataConnectorHealth.length > 0) {
      return;
    }
    this.isLoadingHealth = true;
    try {
      let data = await get(`/ember/procedures/${this.args.procedureId}/data_connector_health`, {
        app_id: this.appService.app.id,
      });
      this.dataConnectorHealth = data.data_connectors || [];
    } catch (e: unknown) {
      console.warn('Failed to load data connector health', e);
    } finally {
      this.isLoadingHealth = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Tasks::ProcedureListItemStats': typeof TasksProcedureListItemStats;
  }
}
